import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bot, CheckCircle2, FileText, Phone, Shield, Sparkles,
  Send, TrendingUp, Users, Zap, Calendar, Activity,
  Check
} from 'lucide-react';

// Subagents that Asha spawns
const subagents = [
  { agent: "Alayna", role: "Claim Submission", task: "Scrubbing and submitting 28 pending claims", image: "https://framerusercontent.com/images/gpvxkPXtZHSDq7LbDrvpyEwKzAk.png", color: "from-violet-500 to-purple-600" },
  { agent: "Sarah", role: "Working Denials", task: "Reviewing 6 denied claims and filing appeals", image: "/assets/sarah.png", color: "from-indigo-500 to-violet-600" },
  { agent: "Alisha", role: "Posting EOBs", task: "Processing and posting 34 EOBs", image: "/assets/alisha.png", color: "from-emerald-500 to-teal-600" },
  { agent: "Laura", role: "Calling Insurance", task: "Following up on 12 outstanding claims", image: "https://framerusercontent.com/images/8s85NGEzWU5LVKALaMlopNKSHg.png", color: "from-cyan-500 to-blue-600" },
  { agent: "Stephanie", role: "Hygiene Recall", task: "Calling 41 patients overdue for hygiene", image: "/assets/stephanie.png", color: "from-amber-500 to-orange-600" },
  { agent: "Kim", role: "Case Acceptance", task: "Reaching out to 9 unaccepted treatment plans", image: "/assets/kim.png", color: "from-pink-500 to-rose-600" },
];

// All features for typewriter paragraph
const allFeatures = [
  "AI Insurance Verification",
  "AI Claim Submission",
  "AI Patient Billing & Statements",
  "AI Denial Management",
  "AI EOB Posting & Reconciliation",
  "AI AR & Collections Follow-Up",
  "AI Insurance Calling",
  "AI Payment Reconciliation",
  "AI Receptionist",
  "AI Hygiene Recall",
  "AI Case Acceptance",
  "AI Claim Scrubbing",
  "AI Schedule Optimizer",
  "AI Patient Intake",
  "AI Practice Manager",
  "AI Patient Experience",
  "AI Fax Manager",
  "AI Copilot",
  "AI Scribe",
  "AI Treatment Planner",
  "AI Revenue Analytics",
  "Scheduling & Appointments",
  "Tooth Charting",
  "Perio Charting",
  "Treatment Planning",
  "Clinical Notes",
  "Patient Ledgers",
  "Patient Check-In / Checkout",
  "Medical History",
  "Screenings",
  "Audit Trails",
  "Document Management",
  "Digital Imaging (SOTA)",
  "Revenue Analytics Suite",
  "Daily Huddles Dashboard",
  "Collections Reporting",
  "Production Tracking",
  "VoIP Phone System",
  "2-Way Patient Messaging",
  "Next-Gen Forms",
  "Intelligent Fax",
  "SMS Campaigns",
  "Email Campaigns",
  "Physical Phones",
  "Online Scheduling",
  "Team Chat",
  "Text-to-Pay",
  "Terminal Card Readers",
  "eClaims Portal",
  "Patient Portal",
  "Marketing & Campaigns",
  "ePrescribing",
];

const userMessage = "Hey Asha, can you take care of all of my admin work from the past 2 days?";

const MobileAdV3HeroSection = () => {
  const [view, setView] = useState('graphic');

  // Chat graphic state
  const [displayText, setDisplayText] = useState('');
  const [chatPhase, setChatPhase] = useState('typing'); // typing, delegating, spawning, done
  const [visibleAgents, setVisibleAgents] = useState(0);
  const chatEndRef = useRef(null);

  // Feature fade-in state
  const [visibleFeatures, setVisibleFeatures] = useState(0);
  const [typewriterStarted, setTypewriterStarted] = useState(false);

  // Auto-scroll chat
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatPhase, visibleAgents]);

  // Chat typing animation
  useEffect(() => {
    if (view !== 'graphic') return;
    let charIndex = 0;
    let typingTimer;
    let phaseTimer;

    if (chatPhase === 'typing') {
      setDisplayText('');
      typingTimer = setInterval(() => {
        charIndex++;
        setDisplayText(userMessage.slice(0, charIndex));
        if (charIndex >= userMessage.length) {
          clearInterval(typingTimer);
          phaseTimer = setTimeout(() => setChatPhase('delegating'), 600);
        }
      }, 30);
    } else if (chatPhase === 'delegating') {
      phaseTimer = setTimeout(() => setChatPhase('spawning'), 1200);
    } else if (chatPhase === 'spawning') {
      // Agents appear one by one — handled by separate effect
    } else if (chatPhase === 'done') {
      phaseTimer = setTimeout(() => setView('features'), 2500);
    }

    return () => { clearInterval(typingTimer); clearTimeout(phaseTimer); };
  }, [chatPhase, view]);

  // Spawn agents one by one
  useEffect(() => {
    if (chatPhase !== 'spawning') return;
    if (visibleAgents >= subagents.length) {
      const timer = setTimeout(() => setChatPhase('done'), 1000);
      return () => clearTimeout(timer);
    }
    const timer = setTimeout(() => {
      setVisibleAgents(prev => prev + 1);
    }, 600);
    return () => clearTimeout(timer);
  }, [chatPhase, visibleAgents]);

  // Start typewriter after features view fades in
  useEffect(() => {
    if (view === 'features' && !typewriterStarted) {
      const timer = setTimeout(() => setTypewriterStarted(true), 600);
      return () => clearTimeout(timer);
    }
  }, [view, typewriterStarted]);

  // Fade in features one by one
  useEffect(() => {
    if (!typewriterStarted || visibleFeatures >= allFeatures.length) return;
    const timer = setTimeout(() => {
      setVisibleFeatures(prev => prev + 1);
    }, 80);
    return () => clearTimeout(timer);
  }, [typewriterStarted, visibleFeatures]);

  const isAnimating = typewriterStarted && visibleFeatures < allFeatures.length;

  return (
    <section className="relative overflow-hidden min-h-screen" style={{ background: 'linear-gradient(180deg, #1a3a8a 0%, #2563eb 25%, #3b82f6 40%, #60a5fa 55%, #93c5fd 68%, #dbeafe 80%, #f0f6ff 90%, #ffffff 100%)' }}>
      {/* Animated light rays */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-[-20%] left-[10%] w-[400px] h-[600px] opacity-[0.15]"
          style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.4) 0%, transparent 100%)', transform: 'rotate(-15deg)', filter: 'blur(40px)' }}
          animate={{ x: [0, 30, 0], opacity: [0.15, 0.2, 0.15] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-[-10%] right-[5%] w-[300px] h-[500px] opacity-[0.1]"
          style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.3) 0%, transparent 100%)', transform: 'rotate(20deg)', filter: 'blur(50px)' }}
          animate={{ x: [0, -20, 0], opacity: [0.1, 0.18, 0.1] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />
        <motion.div className="absolute top-[15%] left-[20%] w-4 h-4 rounded-full bg-white/20" animate={{ y: [0, -20, 0], opacity: [0.2, 0.5, 0.2] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} />
        <motion.div className="absolute top-[25%] right-[25%] w-3 h-3 rounded-full bg-white/15" animate={{ y: [0, -15, 0], opacity: [0.15, 0.4, 0.15] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }} />
        <motion.div className="absolute top-[10%] right-[15%] w-2 h-2 rounded-full bg-white/25" animate={{ y: [0, -25, 0], opacity: [0.2, 0.6, 0.2] }} transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }} />
      </div>

      <div className="relative z-10 max-w-lg mx-auto px-5 pt-14 pb-16">
        {/* Asha Logo */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center justify-center gap-2.5 mb-7"
        >
          <img src="/assets/alogo.png" alt="Asha Dental" className="h-9 w-auto rounded-lg" />
          <span className="font-bold text-xl text-white drop-shadow-sm">Asha Dental</span>
        </motion.div>

        {/* Hero Headline */}
        <div className="text-center mb-3">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="text-[2.75rem] sm:text-6xl font-black leading-[1.0] tracking-[-0.03em] text-white mb-3 drop-shadow-lg"
          >
            The 1st{' '}
            <span className="relative inline-block">
              AI Native
              <motion.div
                className="absolute -bottom-1 left-0 right-0 h-1 rounded-full bg-white/40"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                style={{ transformOrigin: 'left' }}
              />
            </span>
            {' '}PMS
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="text-lg sm:text-xl text-white/80 font-medium"
          >
            Run your entire practice on autopilot
          </motion.p>
        </div>

        {/* Swappable content area — Chat graphic OR feature paragraph */}
        <div className="mt-7">
          <AnimatePresence mode="wait">
            {view === 'graphic' ? (
              <motion.div
                key="chat-graphic"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20, scale: 0.97 }}
                transition={{ duration: 0.6 }}
              >
                <div className="rounded-2xl overflow-hidden border border-white/20 bg-white shadow-[0_20px_80px_-20px_rgba(0,0,0,0.3)]">
                  {/* Top Bar */}
                  <div className="flex items-center justify-center px-4 py-2.5 border-b border-slate-100 bg-slate-50/80">
                    <div className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-[11px] font-medium bg-white text-slate-500 border border-slate-200">
                      <Bot className="w-3 h-3" />
                      <span>Asha</span>
                      <div className="flex items-center gap-1 ml-1.5 px-1.5 py-0.5 rounded-full bg-green-50 text-green-600 text-[9px] font-bold border border-green-100">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                        Online
                      </div>
                    </div>
                  </div>

                  {/* Chat Messages Area */}
                  <div className="p-4 space-y-3 max-h-[380px] overflow-y-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
                    {/* User message bubble */}
                    <div className="flex justify-end">
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.3 }}
                        className="max-w-[85%] px-4 py-3 rounded-2xl rounded-br-md bg-blue-600 text-white"
                      >
                        <p className="text-sm leading-relaxed">
                          {displayText}
                          {chatPhase === 'typing' && (
                            <motion.span
                              animate={{ opacity: [1, 0] }}
                              transition={{ duration: 0.5, repeat: Infinity }}
                              className="inline-block w-0.5 h-4 bg-white/80 ml-0.5 align-middle"
                            />
                          )}
                        </p>
                      </motion.div>
                    </div>

                    {/* Asha delegating message */}
                    <AnimatePresence>
                      {chatPhase !== 'typing' && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="flex gap-2.5"
                        >
                          <div className="shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                            <Sparkles className="w-3.5 h-3.5 text-white" />
                          </div>
                          <div className="max-w-[85%]">
                            <div className="text-[10px] font-semibold text-slate-400 mb-1 ml-1">Asha</div>
                            <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-slate-50 border border-slate-100">
                              <p className="text-sm text-slate-700 leading-relaxed">
                                On it! Let me spin up your team to handle everything from the past 2 days...
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Subagent cards spawning in */}
                    <AnimatePresence>
                      {visibleAgents > 0 && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="space-y-2 ml-9"
                        >
                          {subagents.slice(0, visibleAgents).map((sa, i) => (
                            <motion.div
                              key={sa.agent}
                              initial={{ opacity: 0, x: -15, scale: 0.95 }}
                              animate={{ opacity: 1, x: 0, scale: 1 }}
                              transition={{ duration: 0.3 }}
                              className="flex items-center gap-2.5 p-2.5 rounded-xl border bg-white border-slate-100 shadow-sm"
                            >
                              <div className="relative shrink-0">
                                <img src={sa.image} alt={sa.agent} className="w-7 h-7 rounded-full object-cover ring-2 ring-blue-100" />
                                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center border-[1.5px] border-white">
                                  <Sparkles className="w-1.5 h-1.5 text-white" />
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 mb-0.5">
                                  <span className="text-[11px] font-bold text-slate-900">{sa.agent}</span>
                                  <span className="text-[7px] px-1 py-0.5 rounded bg-blue-50 text-blue-600 font-bold uppercase tracking-wider flex items-center gap-0.5 border border-blue-100">
                                    <Bot className="w-2 h-2" /> AI
                                  </span>
                                  <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-slate-50 text-slate-500 font-medium border border-slate-100">{sa.role}</span>
                                </div>
                                <div className="text-[10px] text-slate-500 truncate">{sa.task}</div>
                              </div>
                              {i < visibleAgents - 1 || chatPhase === 'done' ? (
                                <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                              ) : (
                                <motion.div
                                  animate={{ rotate: 360 }}
                                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                >
                                  <Zap className="w-4 h-4 text-blue-500 shrink-0" />
                                </motion.div>
                              )}
                            </motion.div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <div ref={chatEndRef} />
                  </div>

                  {/* Input bar */}
                  <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/50">
                    <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white border border-slate-200">
                      <span className="text-sm text-slate-300 flex-1">Ask Asha anything...</span>
                      <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                        <Send className="w-3.5 h-3.5 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="features-paragraph"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                {/* Header */}
                <div className="text-center mb-4">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm text-white text-[11px] font-bold border border-white/25 mb-2">
                    <Sparkles className="w-3 h-3" />
                    20+ AI Products in 1 Platform
                  </div>
                </div>

                {/* Features paragraph with fade-in */}
                <p className="text-[14px] leading-relaxed text-white/90 font-medium text-center">
                  {allFeatures.slice(0, visibleFeatures).map((feature, idx) => (
                    <motion.span
                      key={idx}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.25 }}
                    >
                      {feature}{idx < visibleFeatures - 1 ? ', ' : ''}
                    </motion.span>
                  ))}
                  {isAnimating && (
                    <motion.span
                      animate={{ opacity: [1, 0] }}
                      transition={{ duration: 0.4, repeat: Infinity }}
                      className="inline-block w-[2px] h-3.5 bg-white ml-0.5 align-middle"
                    />
                  )}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default MobileAdV3HeroSection;
